import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import Select from 'react-select';
import { addAsset, editAsset, getUsers } from '../../store/actions/assetActions';
import { IoClose } from 'react-icons/io5';

// Add custom Option component for colored squares
const CustomOption = (props) => {
    const { innerRef, innerProps, data, isFocused } = props;
    const getColorForImpact = (impact) => {
        const colors = {
            "insignificant": "#0081A7",
            "minor": "#00AFB9",
            "moderate": "#FED9B7",
            "major": "#F07167",
            "catastrophe": "#b4170b",
            "unassessed": "#CCCCCC"
        };
        return colors[impact] || "#CCCCCC";
    };

    return (
        <div
            ref={innerRef}
            {...innerProps}
            style={{
                display: 'flex',
                alignItems: 'center',
                padding: '6px 8px',
                backgroundColor: isFocused ? '#EEF0ED' : 'white',
                cursor: 'pointer',
            }}
        >
            <div
                style={{
                    width: 12,
                    height: 12,
                    marginRight: 8,
                    backgroundColor: getColorForImpact(data.value),
                    borderRadius: 2,
                }}
            />
            <span>{data.label}</span>
        </div>
    );
};

// Update the CustomSingleValue component
const CustomSingleValue = (props) => {
    const { data } = props;
    const getColorForImpact = (impact) => {
        const colors = {
            "insignificant": "#0081A7",
            "minor": "#00AFB9",
            "moderate": "#FED9B7",
            "major": "#F07167",
            "catastrophe": "#b4170b"
        };
        return colors[impact] || "#CCCCCC";
    };

    return (
        <div style={{ display: 'flex', alignItems: 'center', padding: 0 }}>
            <div
                style={{
                    width: 10,
                    height: 10,
                    marginRight: 5,
                    backgroundColor: getColorForImpact(data.value),
                    borderRadius: 2,
                }}
            />
            <span>{data.label}</span>
        </div>
    );
};

const AssetOverlay = ({ close, asset, addAssetFN, editAssetFN, users, getUsers }) => {
    const [formData, setFormData] = useState({
        name: '',
        category: '',
        func: '',
        status: 'active',
        location: '',
        sensitivity_level: 'internal',
        owner_id: null,
        confidentiality: 'unassessed',
        integrity: 'unassessed',
        availability: 'unassessed',
        // Software specific fields
        vendor: '',
        expiry_date: '',
        license_type: '',
        number_of_users: '',
        contact: '',
        // Hardware specific fields
        serial_number: '',
        warranty_expiry: '',
        model: '',
        assigned_to: '',
        value: 0,
    });

    useEffect(() => {
        if (asset) {
            setFormData({
                ...asset,
                expiry_date: asset.expiry_date ? asset.expiry_date.split('T')[0] : '',
                warranty_expiry: asset.warranty_expiry ? asset.warranty_expiry.split('T')[0] : '',
                owner_id: asset.owner_id || null
            });
        }
    }, [asset]);

    useEffect(() => {
        getUsers();
    }, []);

    const categoryOptions = [
        { value: 'hardware', label: 'Hardware' },
        { value: 'software', label: 'Software' }
    ];

    const statusOptions = [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
        { value: 'retired', label: 'Retired' },
        { value: 'pending', label: 'Pending' }
    ];

    const sensitivityOptions = [
        { value: 'public', label: 'Public' },
        { value: 'internal', label: 'Internal' },
        { value: 'confidential', label: 'Confidential' },
        { value: 'restricted', label: 'Restricted' }
    ];

    const licenseTypeOptions = [
        { value: 'perpetual', label: 'Perpetual' },
        { value: 'subscription', label: 'Subscription' },
        { value: 'trial', label: 'Trial' },
        { value: 'open_source', label: 'Open Source' }
    ];

    const ciaOptions = [
        { value: 'insignificant', label: 'Insignificant', color: "#0081A7" },
        { value: 'minor', label: 'Minor', color: "#00AFB9" },
        { value: 'moderate', label: 'Moderate', color: "#FED9B7" },
        { value: 'major', label: 'Major', color: "#F07167" },
        { value: 'catastrophe', label: 'Catastrophe', color: "#b4170b" }
    ];

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (asset) {
                await editAssetFN(asset.id, formData);
            } else {
                await addAssetFN(formData);
            }
            close();
        } catch (error) {
            console.error('Error saving asset:', error);
        }
    };

    const customStyles = {
        control: (provided) => ({
            ...provided,
            border: 'none',
            backgroundColor: '#EEF0ED',
            padding: '0 8px',
            minHeight: '36px',
            height: '36px',
            outline: 'none',
            transition: 'box-shadow 0.1s',
            boxShadow: 'inset 0 0 0 0 #3b3b3b',
            display: 'flex',
            alignItems: 'center',
            '&:hover': {
                boxShadow: 'inset 0 -2px 0 0 #3b3b3b',
            },
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? '#3b3b3b' : state.isFocused ? '#EEF0ED' : 'white',
            color: state.isSelected ? 'white' : '#3b3b3b',
            padding: '6px 8px',
            fontSize: '14px',
            '&:hover': {
                backgroundColor: '#EEF0ED',
            },
        }),
        singleValue: (provided) => ({
            ...provided,
            color: '#3b3b3b',
            fontSize: '14px',
            padding: '2px 0',
        }),
        menu: (provided) => ({
            ...provided,
            backgroundColor: 'white',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            marginTop: '2px',
        }),
        menuList: (provided) => ({
            ...provided,
            padding: '4px 0',
        }),
        valueContainer: (provided) => ({
            ...provided,
            padding: '0 4px',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
        }),
        indicatorsContainer: (provided) => ({
            ...provided,
            padding: '0 4px',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
        }),
        dropdownIndicator: (provided) => ({
            ...provided,
            padding: '4px',
        }),
    };

    return (
        <div className="p-8">
            <div className="p-6 bg-white rounded-lg">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-1xl font-semibold text-[#3b3b3b]">Asset Details</h1>
                    <div className="absolute top-2 right-2">
                        <IoClose
                            className="w-6 h-6 cursor-pointer text-[#3b3b3b] hover:text-[#3b3b3b]/70"
                            onClick={close}
                        />
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Basic Information */}
                    <div className="mb-6">
                        <input
                            type="text"
                            placeholder="Name"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            className="w-full p-3 rounded mb-4 bg-[#EEF0ED] text-black text-sm"
                            style={{
                                border: 'none',
                                backgroundColor: '#EEF0ED',
                                padding: '8px',
                                outline: 'none',
                                transition: 'box-shadow 0.1s',
                                boxShadow: 'inset 0 0 0 0 #3b3b3b',
                            }}
                            onFocus={(e) => (e.target.style.boxShadow = 'inset 0 -2px 0 0 #3b3b3b')}
                            onBlur={(e) => (e.target.style.boxShadow = 'inset 0 0 0 0 #3b3b3b')}
                            required
                        />

                        <textarea
                            placeholder="Description"
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            className="w-full h-24 p-3 mb-2 rounded bg-[#EEF0ED] text-black text-sm"
                            style={{
                                border: 'none',
                                backgroundColor: '#EEF0ED',
                                padding: '8px',
                                outline: 'none',
                                transition: 'box-shadow 0.1s',
                                boxShadow: 'inset 0 0 0 0 #3b3b3b',
                            }}
                            onFocus={(e) => (e.target.style.boxShadow = 'inset 0 -2px 0 0 #3b3b3b')}
                            onBlur={(e) => (e.target.style.boxShadow = 'inset 0 0 0 0 #3b3b3b')}
                        />
                        <div className="flex flex-col">
                            {/* <label className="text-[#3b3b3b] mb-2 text-sm">Function</label> */}
                            <input
                                type="text"
                                placeholder="Function"
                                value={formData.func}
                                onChange={(e) => setFormData({ ...formData, func: e.target.value })}
                                className="w-full p-2 rounded bg-[#EEF0ED] text-black text-sm"
                                style={{
                                    border: 'none',
                                    backgroundColor: '#EEF0ED',
                                    padding: '8px',
                                    outline: 'none',
                                    transition: 'box-shadow 0.1s',
                                    boxShadow: 'inset 0 0 0 0 #3b3b3b',
                                }}
                                onFocus={(e) => (e.target.style.boxShadow = 'inset 0 -2px 0 0 #3b3b3b')}
                                onBlur={(e) => (e.target.style.boxShadow = 'inset 0 0 0 0 #3b3b3b')}
                            />
                        </div>
                    </div>

                    {/* Category, Function, Status, Location */}
                    <div className="grid grid-cols-4 gap-4 mb-6">
                        <div className="flex flex-col">
                            <label className="text-[#3b3b3b] mb-2 text-sm">Category</label>
                            <Select
                                value={categoryOptions.find(opt => opt.value === formData.category)}
                                onChange={(opt) => setFormData({ ...formData, category: opt.value })}
                                options={categoryOptions}
                                styles={customStyles}
                                className="text-sm"
                                isSearchable={false}
                                required
                            />
                        </div>

                        <div className="flex flex-col">
                            <label className="text-[#3b3b3b] mb-2 text-sm">Status</label>
                            <Select
                                value={statusOptions.find(opt => opt.value === formData.status)}
                                onChange={(opt) => setFormData({ ...formData, status: opt.value })}
                                options={statusOptions}
                                styles={customStyles}
                                className="text-sm"
                                isSearchable={false}
                            />
                        </div>
                        <div className="flex flex-col">
                            <label className="text-[#3b3b3b] mb-2 text-sm">Location</label>
                            <input
                                type="text"
                                value={formData.location}
                                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                                className="w-full p-2 rounded bg-[#EEF0ED] text-black text-sm"
                                style={{
                                    border: 'none',
                                    backgroundColor: '#EEF0ED',
                                    padding: '8px',
                                    outline: 'none',
                                    transition: 'box-shadow 0.1s',
                                    boxShadow: 'inset 0 0 0 0 #3b3b3b',
                                }}
                                onFocus={(e) => (e.target.style.boxShadow = 'inset 0 -2px 0 0 #3b3b3b')}
                                onBlur={(e) => (e.target.style.boxShadow = 'inset 0 0 0 0 #3b3b3b')}
                            />
                        </div>
                        <div className="flex flex-col">
                            <label className="text-[#3b3b3b] mb-2 text-sm">Owner</label>
                            <Select
                                value={users.find(u => u.user_id === formData.owner_id)}
                                onChange={(opt) => setFormData({ ...formData, owner_id: opt ? opt.user_id : null })}
                                options={users}
                                getOptionLabel={option => `${option.first_name} ${option.last_name}`}
                                getOptionValue={option => option.user_id}
                                isClearable
                                styles={customStyles}
                                className="text-sm"
                                isSearchable={false}
                            />
                        </div>
                    </div>

                    <div className="flex gap-4 mb-6">

                        {/* Sensitivity Level (same size as before) */}
                        <div className="flex flex-col" style={{ width: "250px" }}>
                            <label className="text-[#3b3b3b] mb-2 text-sm">Sensitivity Level</label>
                            <Select
                                value={sensitivityOptions.find(opt => opt.value === formData.sensitivity_level)}
                                onChange={(opt) => setFormData({ ...formData, sensitivity_level: opt.value })}
                                options={sensitivityOptions}
                                styles={customStyles}
                                className="text-sm"
                                isSearchable={false}
                            />
                        </div>

                        {/* Value (small, border removed, compact) */}
                        <div className="flex flex-col" style={{ width: "100px" }}>
                            <label className="text-[#3b3b3b] mb-2 text-sm">Value</label>

                            <div className="relative">
                                {/* $ Prefix */}
                                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm text-gray-600 select-none">
                                    $
                                </span>

                                {/* Numeric Input */}
                                <input
                                    type="number"
                                    min="0"
                                    className="
        w-full 
        pl-5 pr-2 py-2    /* pl-5 so text doesn't overlap $ */
        rounded 
        bg-[#EEF0ED] 
        text-black 
        text-sm 
        focus:outline-none 
      "
                                    style={{
                                        border: "none",
                                        boxShadow: "none",
                                        appearance: "none",
                                        WebkitAppearance: "none",
                                        MozAppearance: "textfield"
                                    }}
                                    value={formData.value ?? ""}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            value: e.target.value === "" ? "" : Number(e.target.value),
                                        })
                                    }
                                />
                            </div>
                        </div>


                    </div>






                    {/* CIA Fields */}
                    <div className="grid grid-cols-3 gap-4 mb-6">
                        <div className="flex flex-col">
                            <label className="text-[#3b3b3b] mb-2 text-sm">Confidentiality</label>
                            <Select
                                value={ciaOptions.find(opt => opt.value === formData.confidentiality)}
                                onChange={(opt) => setFormData({ ...formData, confidentiality: opt.value })}
                                options={ciaOptions}
                                styles={customStyles}
                                className="text-sm"
                                components={{
                                    Option: CustomOption,
                                    SingleValue: CustomSingleValue
                                }}
                                isSearchable={false}
                            />
                        </div>
                        <div className="flex flex-col">
                            <label className="text-[#3b3b3b] mb-2 text-sm">Integrity</label>
                            <Select
                                value={ciaOptions.find(opt => opt.value === formData.integrity)}
                                onChange={(opt) => setFormData({ ...formData, integrity: opt.value })}
                                options={ciaOptions}
                                styles={customStyles}
                                className="text-sm"
                                components={{
                                    Option: CustomOption,
                                    SingleValue: CustomSingleValue
                                }}
                                isSearchable={false}
                            />
                        </div>
                        <div className="flex flex-col">
                            <label className="text-[#3b3b3b] mb-2 text-sm">Availability</label>
                            <Select
                                value={ciaOptions.find(opt => opt.value === formData.availability)}
                                onChange={(opt) => setFormData({ ...formData, availability: opt.value })}
                                options={ciaOptions}
                                styles={customStyles}
                                className="text-sm"
                                components={{
                                    Option: CustomOption,
                                    SingleValue: CustomSingleValue
                                }}
                                isSearchable={false}
                            />
                        </div>
                    </div>

                    {/* Software-specific fields */}
                    {formData.category === 'software' && (
                        <div className="grid grid-cols-2 gap-4 mb-6">
                            <div className="flex flex-col">
                                <label className="text-[#3b3b3b] mb-2 text-sm">Vendor</label>
                                <input
                                    type="text"
                                    value={formData.vendor}
                                    onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
                                    className="w-full p-2 rounded bg-[#EEF0ED] text-black text-sm"
                                    style={{
                                        border: 'none',
                                        backgroundColor: '#EEF0ED',
                                        padding: '8px',
                                        outline: 'none',
                                        transition: 'box-shadow 0.1s',
                                        boxShadow: 'inset 0 0 0 0 #3b3b3b',
                                    }}
                                    onFocus={(e) => (e.target.style.boxShadow = 'inset 0 -2px 0 0 #3b3b3b')}
                                    onBlur={(e) => (e.target.style.boxShadow = 'inset 0 0 0 0 #3b3b3b')}
                                />
                            </div>
                            <div className="flex flex-col">
                                <label className="text-[#3b3b3b] mb-2 text-sm">Expiry Date</label>
                                <input
                                    type="date"
                                    value={formData.expiry_date}
                                    onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                                    className="w-full p-2 rounded bg-[#EEF0ED] text-black text-sm"
                                    style={{
                                        border: 'none',
                                        backgroundColor: '#EEF0ED',
                                        padding: '8px',
                                        outline: 'none',
                                        transition: 'box-shadow 0.1s',
                                        boxShadow: 'inset 0 0 0 0 #3b3b3b',
                                    }}
                                    onFocus={(e) => (e.target.style.boxShadow = 'inset 0 -2px 0 0 #3b3b3b')}
                                    onBlur={(e) => (e.target.style.boxShadow = 'inset 0 0 0 0 #3b3b3b')}
                                />
                            </div>
                            <div className="flex flex-col">
                                <label className="text-[#3b3b3b] mb-2 text-sm">License Type</label>
                                <Select
                                    value={licenseTypeOptions.find(opt => opt.value === formData.license_type)}
                                    onChange={(opt) => setFormData({ ...formData, license_type: opt ? opt.value : '' })}
                                    options={licenseTypeOptions}
                                    styles={customStyles}
                                    className="text-sm"
                                    isSearchable={false}
                                />
                            </div>
                            <div className="flex flex-col">
                                <label className="text-[#3b3b3b] mb-2 text-sm">Number of Users</label>
                                <input
                                    type="number"
                                    value={formData.number_of_users}
                                    onChange={(e) => setFormData({ ...formData, number_of_users: e.target.value })}
                                    className="w-full p-2 rounded bg-[#EEF0ED] text-black text-sm"
                                    style={{
                                        border: 'none',
                                        backgroundColor: '#EEF0ED',
                                        padding: '8px',
                                        outline: 'none',
                                        transition: 'box-shadow 0.1s',
                                        boxShadow: 'inset 0 0 0 0 #3b3b3b',
                                    }}
                                    onFocus={(e) => (e.target.style.boxShadow = 'inset 0 -2px 0 0 #3b3b3b')}
                                    onBlur={(e) => (e.target.style.boxShadow = 'inset 0 0 0 0 #3b3b3b')}
                                />
                            </div>
                        </div>
                    )}

                    {/* Hardware-specific fields */}
                    {formData.category === 'hardware' && (
                        <div className="grid grid-cols-2 gap-4 mb-6">
                            <div className="flex flex-col">
                                <label className="text-[#3b3b3b] mb-2 text-sm">Serial Number</label>
                                <input
                                    type="text"
                                    value={formData.serial_number}
                                    onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                                    className="w-full p-2 rounded bg-[#EEF0ED] text-black text-sm"
                                    style={{
                                        border: 'none',
                                        backgroundColor: '#EEF0ED',
                                        padding: '8px',
                                        outline: 'none',
                                        transition: 'box-shadow 0.1s',
                                        boxShadow: 'inset 0 0 0 0 #3b3b3b',
                                    }}
                                    onFocus={(e) => (e.target.style.boxShadow = 'inset 0 -2px 0 0 #3b3b3b')}
                                    onBlur={(e) => (e.target.style.boxShadow = 'inset 0 0 0 0 #3b3b3b')}
                                />
                            </div>
                            <div className="flex flex-col">
                                <label className="text-[#3b3b3b] mb-2 text-sm">Warranty Expiry</label>
                                <input
                                    type="date"
                                    value={formData.warranty_expiry}
                                    onChange={(e) => setFormData({ ...formData, warranty_expiry: e.target.value })}
                                    className="w-full p-2 rounded bg-[#EEF0ED] text-black text-sm"
                                    style={{
                                        border: 'none',
                                        backgroundColor: '#EEF0ED',
                                        padding: '8px',
                                        outline: 'none',
                                        transition: 'box-shadow 0.1s',
                                        boxShadow: 'inset 0 0 0 0 #3b3b3b',
                                    }}
                                    onFocus={(e) => (e.target.style.boxShadow = 'inset 0 -2px 0 0 #3b3b3b')}
                                    onBlur={(e) => (e.target.style.boxShadow = 'inset 0 0 0 0 #3b3b3b')}
                                />
                            </div>
                        </div>
                    )}

                    <div className="flex justify-end space-x-3 pt-4">
                        <button
                            type="button"
                            onClick={close}
                            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 bg-[#3b3b3b] text-white rounded-md hover:bg-opacity-90"
                        >
                            {asset ? 'Save Changes' : 'Add Asset'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const mapStateToProps = (state) => ({
    users: state.asset.users,
});

const mapDispatchToProps = (dispatch) => ({
    addAssetFN: (data) => dispatch(addAsset(data)),
    editAssetFN: (id, data) => dispatch(editAsset(id, data)),
    getUsers: () => dispatch(getUsers())
});

export default compose(connect(mapStateToProps, mapDispatchToProps))(AssetOverlay);